import React, { Component, ErrorInfo, ReactNode } from "react";
import { withRouter, RouteComponentProps } from "react-router-dom";

import { ReactComponent as ErrorIcon } from "src/images/post-check-alert.svg";
import { Button } from "../Button/Button";
import IDHFormattedMessage from "../IDHFormattedMessage/IDHFormattedMessage";
import { AppConfig } from "src/config/AppConfig";

import "./WorkspaceErrorBoundary.scss";

const envName = AppConfig.getEnvironmentName();

const STACK_TRACE_VISIBILITY_ENVS = ["local", "localdev", "dev", "beta"];

interface Props extends RouteComponentProps {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  copied: boolean;
}

class WorkspaceErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, copied: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, copied: false };
  }

  componentDidUpdate(prevProps: Props) {
    // Reset error boundary when route changes
    if (this.props.location.pathname !== prevProps.location.pathname) {
      if (this.state.hasError) {
        this.setState({
          hasError: false,
          error: undefined,
          errorInfo: undefined,
          copied: false,
        });
      }
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Workspace Error:", error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
    // You can also log to an error reporting service here
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    window.location.href = "/workspace/";
  };

  handleReload = () => {
    window.location.reload();
  };

  handleCopyError = () => {
    const errorText = `${this.state.error?.toString()}\nURL: ${window.location.href}\n${this.state.errorInfo?.componentStack || ""}`;
    navigator.clipboard.writeText(errorText).then(
      () => {
        this.setState({ copied: true });
        // Reset the copied state after 2 seconds
        setTimeout(() => {
          this.setState({ copied: false });
        }, 2000);
      },
      (err) => {
        console.error("Failed to copy error stack:", err);
      },
    );
  };

  render() {
    if (this.state.hasError) {
      const shouldShowStackTrace =
        STACK_TRACE_VISIBILITY_ENVS.includes(envName);

      return (
        <div className="workspace-error-boundary">
          <div className="workspace-error-boundary__content">
            <header className="workspace-error-boundary__header">
              <div className="workspace-error-boundary__icon">
                <ErrorIcon />
              </div>
              <h1 className="workspace-error-boundary__title text-h3-medium">
                <IDHFormattedMessage
                  id="ws_global_error_heading"
                  defaultMessage="Something went wrong"
                />
              </h1>
            </header>
            <p className="workspace-error-boundary__message text-h7-regular">
              <IDHFormattedMessage
                id="ws_global_error_message"
                defaultMessage="We encountered an error while loading this page. Please try reloading the page or check back later."
              />
            </p>
            {!shouldShowStackTrace && this.state.error && (
              <div className="workspace-error-boundary__error-container">
                <pre className="workspace-error-boundary__error-stack text-h7-regular">
                  {window.location.href}
                </pre>
              </div>
            )}
            {shouldShowStackTrace && this.state.error && (
              <details className="workspace-error-boundary__details" open>
                <summary className="text-h6-medium">
                  Error details (development only)
                </summary>
                <div className="workspace-error-boundary__error-container">
                  <pre className="workspace-error-boundary__error-stack text-h7-regular">
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </div>
              </details>
            )}
            <div className="workspace-error-boundary__actions">
              {shouldShowStackTrace && this.state.error && (
                <Button
                  variant="white-with-light-grey-border"
                  size="large"
                  onClick={this.handleCopyError}
                >
                  {this.state.copied
                    ? "Stack trace copied"
                    : "Copy stack trace"}
                </Button>
              )}
              <Button variant="blue" size="large" onClick={this.handleReload}>
                <IDHFormattedMessage
                  id="ws_reload_page"
                  defaultMessage="Reload page"
                />
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default withRouter(WorkspaceErrorBoundary);
